import { Outlet, Link, useRouterState } from '@tanstack/react-router';
import { BarChart3, Calendar, Home, LogOut, Megaphone, Settings, Shield } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '@/lib/authStore';

const nav = [
  { to: '/', label: 'Dashboard', icon: Home },
  { to: '/boosts', label: 'Boosts', icon: Megaphone },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/calendar', label: 'Calendar', icon: Calendar },
  { to: '/settings', label: 'Settings', icon: Settings },
] as const;

export function AppShell() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const path = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 border-r border-border bg-surface px-4 py-6 lg:flex lg:flex-col">
        <Link to="/" className="mb-8 flex items-center gap-2 px-2 text-lg font-bold">
          <span className="text-2xl">🌙</span>
          <span>Nightlife</span>
          <span className="ml-1 rounded-full bg-brand-800/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-400">
            Dashboard
          </span>
        </Link>

        <nav className="flex-1 space-y-1">
          {nav.map((item) => {
            const active = path === item.to || (item.to !== '/' && path.startsWith(item.to));
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={clsx(
                  'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'bg-brand-800/30 text-brand-400'
                    : 'text-text-secondary hover:bg-surface-elevated hover:text-text-primary',
                )}
              >
                <Icon size={18} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Admin paneli linki — yalnız admin role görüyor. Filament ayrı bir uygulama
            olduğu için <a href> ile harici geçiş yapıyoruz. */}
        {user?.role === 'admin' && (
          <a
            href="/admin"
            className="mt-4 flex items-center gap-3 rounded-xl border border-brand-400/40 bg-brand-800/30 px-3 py-2 text-sm font-semibold text-brand-300 transition-colors hover:bg-brand-800/50 hover:text-brand-200"
          >
            <Shield size={18} />
            Admin Paneli
          </a>
        )}

        <div className="mt-6 border-t border-border-subtle pt-4">
          <div className="px-2 text-sm">
            <p className="font-semibold">{user?.name}</p>
            <p className="text-text-muted">@{user?.username}</p>
            {user?.role === 'admin' && (
              <span className="mt-1 inline-block rounded-full bg-brand-800/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-400">
                Admin
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className="mt-3 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-elevated hover:text-danger"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto px-6 py-8 lg:px-10">
        <Outlet />
      </main>
    </div>
  );
}
